import { useCallback, useEffect, useState } from 'react'

/**
 * Light or dark, per browser. The choice is a viewer convenience, not data, so
 * it lives in localStorage; with no choice saved, the Mac's own setting wins.
 * index.html applies the same rule before first paint, so there is no flash.
 */
export type Theme = 'light' | 'dark'

const KEY = 'studio-theme'
const THEME_COLOR: Record<Theme, string> = { light: '#fdfcf9', dark: '#1f1e1c' }

function stored(): Theme | null {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' ? value : null
  } catch {
    return null
  }
}

function system(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function apply(theme: Theme) {
  document.documentElement.dataset['theme'] = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme])
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => stored() ?? system())

  useEffect(() => {
    apply(theme)
  }, [theme])

  // Follow the Mac's setting live, until a choice has been made here.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      if (!stored()) setThemeState(system())
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(KEY, next)
    } catch {
      // Private browsing: the choice lasts for this visit only.
    }
    setThemeState(next)
  }, [])

  return { theme, setTheme, toggle: () => setTheme(theme === 'dark' ? 'light' : 'dark') }
}

/**
 * The theme colour (Settings > Theme): the colour used wherever the Studio
 * would use blue. Per browser, like light and dark; index.html applies it
 * before first paint. The shades for each live in tokens.css.
 */
export const ACCENTS = ['blue', 'purple', 'pink', 'red', 'orange', 'green', 'teal', 'lime', 'slate', 'neutral'] as const
export type Accent = (typeof ACCENTS)[number]

export const ACCENT_LABEL: Record<Accent, string> = {
  blue: 'Blue',
  purple: 'Purple',
  pink: 'Pink',
  red: 'Red',
  orange: 'Orange',
  green: 'Green',
  teal: 'Teal',
  lime: 'Olive',
  slate: 'Slate',
  neutral: 'Neutral',
}

/** The light-mode shade, for the swatches. */
export const ACCENT_SWATCH: Record<Accent, string> = {
  blue: '#0c66e4',
  purple: '#6e5dc6',
  pink: '#ae4787',
  red: '#c9372c',
  orange: '#c25100',
  green: '#1f845a',
  teal: '#1d7f8c',
  lime: '#4c6b1f',
  slate: '#44546f',
  neutral: '#1c1b19',
}

const ACCENT_KEY = 'studio-accent'

function storedAccent(): Accent {
  try {
    const value = localStorage.getItem(ACCENT_KEY)
    return (ACCENTS as readonly string[]).includes(value ?? '') ? (value as Accent) : 'blue'
  } catch {
    return 'blue'
  }
}

export function useAccent() {
  const [accent, setAccentState] = useState<Accent>(storedAccent)

  useEffect(() => {
    if (accent === 'blue') delete document.documentElement.dataset['accent']
    else document.documentElement.dataset['accent'] = accent
  }, [accent])

  const setAccent = useCallback((next: Accent) => {
    try {
      localStorage.setItem(ACCENT_KEY, next)
    } catch {
      // Private browsing: the choice lasts for this visit only.
    }
    setAccentState(next)
  }, [])

  return { accent, setAccent }
}
